import { useState, useEffect } from 'react';
import { colors, spacing } from '../theme';
import Icon from './Icon';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';

const styles = {
  container: {
    maxWidth: '100%',
    maxHeight: '70vh',
    overflow: 'auto',
  },
  image: {
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
  },
  pdfContainer: {
    width: '100%',
    height: '70vh',
    border: `1px solid ${colors.gray200}`,
    borderRadius: '19px',
  },
  textPreview: {
    padding: spacing.lg,
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    backgroundColor: colors.gray50,
    borderRadius: '19px',
    whiteSpace: 'pre-wrap',
    maxHeight: '400px',
    overflow: 'auto',
  },
  unsupported: {
    padding: spacing.xl,
    textAlign: 'center',
    color: colors.midGray,
  },
  actions: {
    display: 'flex',
    gap: spacing.md,
    marginTop: spacing.md,
    justifyContent: 'center',
  },
};

  // Supported file types for preview
  const supportedTypes = {
    images: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'],
    pdf: ['pdf'],
    text: ['txt', 'csv', 'json', 'xml', 'html', 'css', 'js', 'md'],
  };

// Helper functions moved outside component to avoid dependency warnings
  const isImageType = (type, ext) => {
    if (type?.startsWith('image/')) return true;
    return ext && supportedTypes.images.includes(ext.toLowerCase());
  };

  const isPdfType = (type, ext) => {
    if (type === 'application/pdf') return true;
    return ext && ext.toLowerCase() === 'pdf';
  };

  const isTextType = (type, ext) => {
    if (type?.startsWith('text/')) return true;
    if (type === 'application/json' || type === 'application/xml') return true;
    return ext && supportedTypes.text.includes(ext.toLowerCase());
  };

function DocumentPreview({ fileUrl, fileName, fileType, fileExtension, onDownload, onClose }) {
  const [previewContent, setPreviewContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!fileUrl) return;

    setLoading(true);
    setError(null);

    const loadPreview = async () => {
      try {
        if (isImageType(fileType, fileExtension)) {
          // Image preview
          setPreviewContent('image');
        } else if (isPdfType(fileType, fileExtension)) {
          // PDF preview - embed iframe
          setPreviewContent('pdf');
        } else if (isTextType(fileType, fileExtension)) {
          // Text preview
          try {
            const response = await fetch(fileUrl);
            if (!response.ok) throw new Error('Failed to fetch');
            const text = await response.text();
            setPreviewContent({ type: 'text', content: text });
          } catch (fetchErr) {
            console.error('Error fetching text file:', fetchErr);
            setPreviewContent('unsupported');
          }
        } else {
          setPreviewContent('unsupported');
        }
      } catch (err) {
        console.error('Error loading preview:', err);
        setError('Failed to load preview');
        setPreviewContent('unsupported');
      } finally {
        setLoading(false);
      }
    };

    loadPreview();
  }, [fileUrl, fileType, fileExtension]);

  if (loading) {
    return <LoadingSpinner text="Loading preview..." />;
  }

  if (error) {
    return (
      <div style={styles.unsupported}>
        <Icon name="error" type="status" size={48} style={{ marginBottom: spacing.md }} />
        <p>{error}</p>
      </div>
    );
  }

  const renderPreview = () => {
    if (previewContent === 'image') {
      return <img src={fileUrl} alt={fileName} style={styles.image} />;
    }

    if (previewContent === 'pdf') {
      return (
        <iframe
          src={fileUrl}
          title={fileName}
          style={styles.pdfContainer}
          aria-label={`PDF preview of ${fileName}`}
        />
      );
    }

    if (previewContent?.type === 'text') {
      return (
        <div style={styles.textPreview}>
          {previewContent.content.substring(0, 10000)}
          {previewContent.content.length > 10000 && '...'}
        </div>
      );
    }

    return (
      <div style={styles.unsupported}>
        <Icon name="document" type="document" size={48} style={{ marginBottom: spacing.md }} />
        <p style={{ fontWeight: 500, marginBottom: spacing.sm }}>Preview not available for this file type.</p>
        <p style={{ fontSize: '0.875rem', marginBottom: spacing.md }}>
          You can download the file to view it.
        </p>
        <div style={{ marginTop: spacing.lg, padding: spacing.md, backgroundColor: colors.gray100, borderRadius: '8px', textAlign: 'left' }}>
          <p style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: spacing.xs, color: colors.darkGray }}>
            Supported preview types:
          </p>
          <ul style={{ fontSize: '0.875rem', color: colors.midGray, margin: 0, paddingLeft: '1.5rem', lineHeight: 1.8 }}>
            <li><strong>Images:</strong> JPG, PNG, GIF, WebP, SVG, BMP</li>
            <li><strong>Documents:</strong> PDF</li>
            <li><strong>Text files:</strong> TXT, CSV, JSON, XML, HTML, CSS, JS, MD</li>
          </ul>
          <p style={{ fontSize: '0.75rem', color: colors.midGray, marginTop: spacing.sm, marginBottom: 0, fontStyle: 'italic' }}>
            Other file types (Word, Excel, PowerPoint, etc.) can be downloaded and opened with their respective applications.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={styles.container}>{renderPreview()}</div>
      <div style={styles.actions}>
        {onDownload && (
          <Button onClick={onDownload} variant="primary" icon={<Icon name="download" type="action" size={18} />}>
            Download
          </Button>
        )}
        <Button
          onClick={() => window.open(fileUrl, '_blank')}
          variant="outline"
          icon={<Icon name="document" type="document" size={18} />}
        >
          Open in New Tab
        </Button>
      </div>
    </div>
  );
}

export default DocumentPreview;

